import type { ComponentType } from 'react';
import { Badge, type BadgeVariant } from './Badge';
import styles from './IconCountBadge.module.css';

// The icon-count badge — a DS Badge whose whole label is a 12px status glyph beside a BARE
// NUMERAL. It is what the four summary tiles (Tasks · Routines · Files · Apps) wear on Your
// Agents and on a Space's Dashboard tab, on every viewport.
//
// Figma `15378:117541` — the frame's own note reads "Use 12px icons for badges": the tiles'
// badges lost their text labels, so four statuses fit a 249px tile instead of two.
//
// This mirrors native's `IconCountBadge` (iOS `DesignSystem/Components.swift`, Android
// `ui/Components.kt`) 1:1 — 12px glyph, Spacing/2 to the number, and the Badge's own 8px side
// padding / 20 height / rounded-8 / Style fill. One atom, three surfaces.
//
// The glyph goes in the Badge's `label` slot rather than its `startIcon` slot on purpose: that
// slot is a 16px box with a 6px gap to the label, which reads as far too much air between a
// glyph and the number it counts.
//
// **The words are not lost, only the pixels.** The localized "{count} Completed" rides
// `aria-label`, so a screen reader still hears what the colour and the glyph stand for.
export function IconCountBadge({
  icon: Icon,
  count,
  variant,
  label,
}: {
  icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  count: number;
  variant: BadgeVariant;
  /** The localized sentence the glyph replaces — announced instead of the bare numeral. */
  label: string;
}) {
  return (
    <Badge
      variant={variant}
      label={
        <span className={styles.count}>
          <Icon size={12} aria-hidden />
          {count}
        </span>
      }
      aria-label={label}
    />
  );
}

/** The cluster the tiles wear: badges 2px apart (Figma Spacing/2 ≡ native's `HStack(spacing: 2)`). */
export function IconCountBadges({ children }: { children: React.ReactNode }) {
  return <span className={styles.cluster}>{children}</span>;
}
